import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal, Button as BButton, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUserPlus, 
    faDownload,
    faCog
} from '@fortawesome/free-solid-svg-icons';
import swal from 'sweetalert';
import { createActionAction, updateActionAction, deleteParamAction, getActionsAction, startLoader } from '../../../store/actions/UserAction';
import { currentUser } from "../../../store/selectors/CurrentUserSelector";
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../DataTables/DataTableBase';
import { useTranslation } from "react-i18next";

const Action = (props) => {
    const { t } = useTranslation();
    let errorsObj = { name: '', code: '' };
    
    // Form states
    const [nameFr, setNameFr] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [code, setCode] = useState('');
    const [errors, setErrors] = useState(errorsObj);
    const [id, setId] = useState('');
    const [updateMode, setUpdateMode] = useState(false);
    
    // Modal states
    const [addCard, setAddCard] = useState(false);

    // Filter states
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    };

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <Button 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={(e) => onGlobalFilterChange(e)} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    };

    const clearFilter = () => {
        initFilters();
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const setColumns = () => {
        return [
            {name: 'code', headerName: "Code", filterPlaceholder: 'Filtrer par code', style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'nameFr', headerName: t('common.name'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: 'Créé le', filterPlaceholder: t('facturation.create-date-filter'), style: { minWidth: '6rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '6rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    };

    const getActionsTemplate = (rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAMUSER') && canDo('MENPARAMUSER', 'UPDATEACTION') && (
                    <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={e => updateAction(rowData)}
                        title={t('common.edit')}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAMUSER') && canDo('MENPARAMUSER', 'DELETEACTION') && (
                    <button 
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t('common.delete')}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    };

    const handleDelete = (rowData) => {
        swal({
            title: "Supprimer l'action " + rowData.nameFr + " ?",
            text: "Une fois l'action supprimée, elle ne pourra plus être accessible",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                let data = { id: rowData.id };
                props.dispatch(startLoader());
                props.dispatch(deleteParamAction('action', data));
            }
        });
    };

    const dateFilterTemplate = (options) => {
        return <Calendar 
            value={options.value} 
            onChange={(e) => options.filterCallback(e.value, options.index)} 
            dateFormat="dd/mm/yy" 
            placeholder="dd/mm/yyyy" 
            mask="99/99/9999" 
        />;
    };

    const datePaiementBodyTemplate = (rowData) => {
        return formatDate(rowData.createdAt);
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const canDo = (menuCode, actionCode) => {
        let theMenu = props.currentUser.menus?.find(menu => menu.code === menuCode);
        if(!theMenu){
            return false;
        }else{
            return theMenu.actionMenus?.filter(action => action.code === actionCode).length > 0;
        }
    }
    
    const hasMenu = (menuCode) => {
        return props.currentUser.menus?.filter(menu => menu.code === menuCode).length > 0;
    }

    useEffect(() => {
       props.dispatch(getActionsAction());
       initFilters();    
    }, []);

    const resetForm = () => {
        setId("");
        setNameFr("");
        setNameEn("");
        setCode("");
        setErrors(errorsObj);
        setUpdateMode(false);
    };

    function onActionSave(e) {
        e.preventDefault();
        let error = false;
        const errorObj = { ...errorsObj };
        if (code === '') {
            errorObj.code = t('common.code-required');
            error = true;
        }
        if (nameFr === '') {
            errorObj.name = t('common.name-required');
            error = true;
        }
        
        setErrors(errorObj);
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        props.dispatch(startLoader());
        
        let obj = { nameFr: nameFr, nameEn: nameFr, code: code };
        if(updateMode) {
            props.dispatch(updateActionAction(id, obj));
        }else{
            props.dispatch(createActionAction(obj));
        }
        
        setAddCard(false);
        resetForm();
    }

    const updateAction = (data) => {
        setId(data.id);
        setNameFr(data.nameFr);
        setNameEn(data.nameEn);
        setCode(data.code);
        setUpdateMode(true);
        setAddCard(true);
    }

    useEffect(() => {
        resetForm();
        setAddCard(false);
    }, [props.actions]);

    const newActionCreate = () => {
        resetForm();
        setAddCard(true);
    }

    const handleDownloadTemplate = () => {
        console.log('Download action template');
    };

    return (
        <>
            {/* Header */}
            <div className="provider-type-header mb-0 form-head bg-white p-3 bt-5 border-top-solid">
                <div>
                    <h4 className="mb-1">
                        <FontAwesomeIcon icon={faCog} className="me-2" />
                        {t('user.actions')}
                    </h4>
                    <p className="text-muted mb-0 small">{t('common.manage-system-actions')}</p>
                </div>
                <div className="d-flex gap-2">
                    {/* <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleDownloadTemplate}
                    >
                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                        {t("common.download-template")}
                    </button> */}
                    
                    {hasMenu('MENPARAMUSER') && canDo('MENPARAMUSER', 'CREATEACTION') && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={newActionCreate}
                        >
                            <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                            Nouvelle action
                        </button>
                    )}
                </div>
            </div>

            {/* Modal for creating/editing action */}
            <Modal 
                show={addCard} 
                onHide={() => setAddCard(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {!updateMode ? "Créer une nouvelle action" : `Modifier l'action ${nameFr}`}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form onSubmit={onActionSave}>
                        <div className="mb-3">
                            <label className="form-label">
                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                {t('common.code')} de l'action
                            </label>
                            <input
                                type='text'
                                className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                placeholder='CREATE_USER'
                                value={code}
                                disabled={props.showLoading}
                                onChange={(e) => setCode(e.target.value)}
                            />
                            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">{t('common.label')} Fr</label>
                            <input
                                type='text'
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder='Créer un utilisateur'
                                value={nameFr}
                                disabled={props.showLoading}
                                onChange={(e) => setNameFr(e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddCard(false)}
                        disabled={props.showLoading}
                    >
                        {t('common.cancel')}
                    </button>
                    
                    {props.showLoading ? (
                        <BButton variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t('common.saving')}</span>
                        </BButton>
                    ) : (
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            onClick={onActionSave}
                        >
                            {t('common.save')}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={props.actions ? props.actions.map(action => {
                            action.createdAt = new Date(action.createdAt);
                            return action;
                        }) : []}
                        emptyMessage="Aucune action trouvée" 
                        filters={filters}
                        globalFiltersFields={['code', 'nameFr', 'createdAt']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: 'Code', dataKey: 'code' },
                            { title: t('common.name'), dataKey: 'nameFr' },
                            { title: t('common.create-date'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename={"actions"}
                        loading={props.showLoading}
                    />
                </div>
            </div>

            {/* Custom CSS */}
            <style jsx>{`
                .provider-type-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border-radius: 8px 8px 0 0;
                }
                
                .provider-type-header h4 {
                    color: white;
                    font-weight: 600;
                }
                
                .provider-type-header p {
                    color: rgba(255, 255, 255, 0.8);
                }
                
                .provider-type-header .btn-outline-primary {
                    border-color: rgba(255, 255, 255, 0.5);
                    color: white;
                }
                
                .provider-type-header .btn-outline-primary:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                    border-color: white;
                }
                
                .provider-type-header .btn-primary {
                    background-color: rgba(255, 255, 255, 0.2);
                    border-color: rgba(255, 255, 255, 0.3);
                    color: white;
                }
                
                .provider-type-header .btn-primary:hover {
                    background-color: rgba(255, 255, 255, 0.3);
                    border-color: rgba(255, 255, 255, 0.5);
                }
                
                /* Button improvements */
                .btn-sm {
                    padding: 0.25rem 0.5rem;
                    font-size: 0.875rem;
                    border-radius: 0.25rem;
                    transition: all 0.2s ease;
                }
                
                .btn-outline-primary:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0,123,255,0.2);
                }
                
                .btn-outline-danger:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(220,53,69,0.2);
                }
                
                /* Form validation styling */
                .is-invalid {
                    border-color: #dc3545;
                }
                
                .invalid-feedback {
                    display: block;
                    width: 100%;
                    margin-top: 0.25rem;
                    font-size: 0.875em;
                    color: #dc3545;
                }
                
                /* Form label improvements */
                .form-label {
                    font-weight: 500;
                    color: #495057;
                    margin-bottom: 0.5rem;
                }
                
                /* Input improvements */
                .form-control {
                    border: 1px solid #ced4da;
                    border-radius: 0.375rem;
                    padding: 0.5rem 0.75rem;
                    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                }
                
                .form-control:focus {
                    border-color: #80bdff;
                    outline: 0;
                    box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
                }
                
                /* Modal improvements */
                .modal-header {
                    background-color: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                }
                
                .modal-title {
                    font-weight: 600;
                    color: #495057;
                }
                
                /* Responsive design */
                @media (max-width: 768px) {
                    .provider-type-header {
                        flex-direction: column;
                        gap: 1rem;
                    }
                    
                    .provider-type-header .d-flex {
                        flex-direction: column;
                        width: 100%;
                    }
                }
            `}</style>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        actions: state.user.actions,
        errorMessage: state.user.errorMessage,
        successMessage: state.user.successMessage,
        showLoading: state.user.showLoading,
        currentUser: currentUser(state)
    };
};

export default connect(mapStateToProps)(Action);